import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import InitialsAvatar from '../../components/InitialsAvatar';
import { PageLoader, ErrorState, EmptyState } from '../../components/ui';
import { membersApi } from '../../api/resources';
import { useToast } from '../../context/ToastContext';

/** True when two id sequences differ (order- or membership-wise). */
function orderChanged(a, b) {
  if (a.length !== b.length) return true;
  return a.some((id, i) => id !== b[i]);
}

export default function AdminBoardOrder() {
  const toast = useToast();

  const [state, setState] = useState({ loading: true, error: null });
  const [rows, setRows] = useState([]); // working order (local)
  const [savedIds, setSavedIds] = useState([]); // last persisted order (from API)
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setState({ loading: true, error: null });
    membersApi
      .list({ leadership: 1, per_page: 100 })
      .then((res) => {
        const items = res.items || [];
        setRows(items);
        setSavedIds(items.map((m) => m.id));
        setState({ loading: false, error: null });
      })
      .catch((error) => setState({ loading: false, error }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function move(index, delta) {
    setRows((list) => {
      const next = [...list];
      const target = index + delta;
      if (target < 0 || target >= next.length) return list;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  const orderedIds = rows.map((m) => m.id);
  const dirty = orderChanged(orderedIds, savedIds);

  async function handleSave() {
    setBusy(true);
    try {
      const updated = await membersApi.saveBoardOrder(orderedIds);
      // Sync working + saved state from the server's authoritative order.
      setRows(updated);
      setSavedIds(updated.map((m) => m.id));
      toast.success('Board order saved.');
    } catch (err) {
      if (err?.errors && Object.keys(err.errors).length) {
        const first = Object.values(err.errors)[0];
        toast.error((Array.isArray(first) ? first[0] : first) || err.message);
      } else {
        toast.error(err?.message || 'Could not save the board order.');
      }
    } finally {
      setBusy(false);
    }
  }

  function handleReset() {
    // Restore the last-saved order without a round-trip.
    setRows((list) => {
      const byId = new Map(list.map((m) => [m.id, m]));
      return savedIds.map((id) => byId.get(id)).filter(Boolean);
    });
  }

  return (
    <div className="space-y-6">
      <Seo title="Board display order" />

      <div>
        <Link to="/admin/members" className="text-sm text-ink-muted hover:text-maroon-700">
          ← Back to members
        </Link>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Board display order</h1>
            <p className="mt-1 max-w-2xl text-sm text-ink-muted">
              Arrange how leadership members appear on the public{' '}
              <Link to="/leadership" className="text-maroon-700 underline-offset-2 hover:underline">
                Meet Area 1 Officers &amp; Presidents
              </Link>{' '}
              page. Use the arrows to reorder, then save.
              <span className="mt-1 block font-medium text-ink-soft">
                #1 appears as the featured leader on Meet Area 1 Officers &amp; Presidents.
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dirty && (
              <button onClick={handleReset} className="btn-ghost" disabled={busy}>
                Reset
              </button>
            )}
            <button onClick={handleSave} className="btn-primary" disabled={busy || !dirty}>
              {busy ? 'Saving…' : 'Save order'}
            </button>
          </div>
        </div>
      </div>

      {dirty && !state.loading && (
        <p className="rounded-lg bg-gold-50 px-4 py-2 text-sm text-ink-soft ring-1 ring-gold-200">
          You have unsaved changes. Click <strong>Save order</strong> to publish them.
        </p>
      )}

      {state.loading ? (
        <PageLoader label="Loading board…" />
      ) : state.error ? (
        <ErrorState error={state.error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No leadership members"
          message="Mark members as leadership on the Members page to arrange the board order."
        />
      ) : (
        <ul className="card divide-y divide-cream-300">
          {rows.map((m, i) => (
            <li key={m.id} className="flex items-center gap-4 px-4 py-3 sm:px-5">
              <span className="w-7 shrink-0 text-center font-display text-lg font-bold text-maroon-700">
                {i + 1}
              </span>
              <div className="h-11 w-11 shrink-0 overflow-hidden rounded-full ring-1 ring-ink/[0.06]">
                <InitialsAvatar name={m.full_name} photoPath={m.photo_url || m.photo_path} size="sm" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-ink">{m.full_name}</div>
                <div className="truncate text-xs text-ink-muted">
                  {[m.position, m.club_name].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0 || busy}
                  aria-label={`Move ${m.full_name} up`}
                  className="btn-ghost px-2.5 py-1.5 text-base leading-none disabled:opacity-40"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === rows.length - 1 || busy}
                  aria-label={`Move ${m.full_name} down`}
                  className="btn-ghost px-2.5 py-1.5 text-base leading-none disabled:opacity-40"
                >
                  ↓
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-ink-muted">
        Wired to <code>PUT /api/members/board-order</code>. Saving sends the complete leadership
        list in this order; the public board reflects it immediately.
      </p>
    </div>
  );
}
